/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { invoiceService } from '../../services/api';
import { Button } from '../console/ui';
import BillingModal, { Notice } from './BillingModal';

type Preset = 'last_month' | 'this_month' | 'last_quarter';

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');

const GenerateInvoiceModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const qc = useQueryClient();
  const lastMonth = subMonths(new Date(), 1);
  const [startDate, setStartDate] = useState(ymd(startOfMonth(lastMonth)));
  const [endDate, setEndDate] = useState(ymd(endOfMonth(lastMonth)));

  const generate = useMutation({
    mutationFn: () => invoiceService.generate(startDate, endDate),
    onSuccess: (response) => {
      toast.success(`Invoice ${response.data.invoice_number} generated successfully!`);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to generate invoice');
    },
  });

  const preset = (p: Preset) => {
    const now = new Date();
    if (p === 'last_month') {
      const l = subMonths(now, 1);
      setStartDate(ymd(startOfMonth(l)));
      setEndDate(ymd(endOfMonth(l)));
    } else if (p === 'this_month') {
      setStartDate(ymd(startOfMonth(now)));
      setEndDate(ymd(now));
    } else {
      setStartDate(ymd(startOfMonth(subMonths(now, 3))));
      setEndDate(ymd(endOfMonth(subMonths(now, 1))));
    }
  };

  return (
    <BillingModal open={open} title="Generate invoice" onClose={onClose}>
      <div className="flex flex-col gap-2">
        <span className="text-[13px] text-ps-muted">Quick select</span>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="ps-chip" onClick={() => preset('last_month')}>
            Last month
          </button>
          <button type="button" className="ps-chip" onClick={() => preset('this_month')}>
            This month
          </button>
          <button type="button" className="ps-chip" onClick={() => preset('last_quarter')}>
            Last quarter
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-[13px] text-ps-muted">Start date</span>
          <input type="date" className="ps-input" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-[13px] text-ps-muted">End date</span>
          <input
            type="date"
            className="ps-input"
            value={endDate}
            min={startDate}
            max={ymd(new Date())}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
      </div>
      {startDate && endDate && (
        <Notice tone="info">
          An invoice will be generated for all resource usage from {format(new Date(startDate), 'd MMM yyyy')} to{' '}
          {format(new Date(endDate), 'd MMM yyyy')}.
        </Notice>
      )}
      <div className="flex gap-2.5 justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending || !startDate || !endDate}>
          {generate.isPending ? 'Generating…' : 'Generate invoice'}
        </Button>
      </div>
    </BillingModal>
  );
};

export default GenerateInvoiceModal;
